/**
 * Feb 27, 2026 · Opening Stock Entry
 *
 * Dedicated screen for capturing raw-material opening balances at go-live
 * (or when introducing a new RM mid-year). Every entry writes an audited
 * inventory_txns row with txn_type='Opening', a Passed inventory_lot at
 * the chosen godown, and bumps raw_materials.current_stock atomically —
 * so the Stock Book, Stock Ledger, and Low-Stock alerts all reflect it
 * on the very next tick.
 *
 * Menu · Purchase & Inventory → Opening Stock Entry.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PackageIcon, ArrowsClockwiseIcon, TrashIcon, WarningIcon, PlusIcon, LockKeyIcon } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const today = () => new Date().toISOString().slice(0, 10);

const inpCls = "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const labelCls = "text-[10px] uppercase tracking-widest text-zinc-500 block mb-1";

export default function OpeningStockEntry() {
  const [rms, setRms] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [entries, setEntries] = useState([]);
  const [orders, setOrders] = useState([]);          // Feb 2026 · SO + ESO union for Reserve-for-Order picker
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [form, setForm] = useState({
    rm_id: "", godown_id: "", batch_no: "",
    qty: "", opening_date: today(), remarks: "",
    reserved_for_order: "",   // "SO:<id>" | "ESO:<id>" | ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const [rmRes, gdRes, entRes, lockRes, soRes, esoRes] = await Promise.all([
        api.get("/raw-materials/"),
        api.get("/godowns/"),
        api.get("/opening-stock-entries/"),
        api.get("/opening-stock-entries/lock-status").catch(() => ({ data: { locked: false } })),
        api.get("/sales-orders/").catch(() => ({ data: [] })),
        api.get("/export-sales-orders/").catch(() => ({ data: [] })),
      ]);
      // Mar 2026 · Universal Deactivation Filter — new Opening
      // balances can only be posted against Active RMs.
      setRms((rmRes.data || []).filter(x => x.is_active !== false));
      setGodowns(gdRes.data || []);
      setEntries(entRes.data || []);
      setLocked(!!lockRes.data?.locked);
      const soRows = (soRes.data || []).map(s => ({
        key: `SO:${s.id}`, type: "SO", id: s.id,
        no: s.order_no || s.so_no || "—", customer: s.customer_name || "",
      }));
      const esoRows = (esoRes.data || []).map(s => ({
        key: `ESO:${s.id}`, type: "ESO", id: s.id,
        no: s.order_no || s.eso_no || "—", customer: s.customer_name || s.customer || "",
      }));
      setOrders([...soRows, ...esoRows].sort((a, b) => a.no.localeCompare(b.no)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Opening Stock data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rmMap = useMemo(() => Object.fromEntries((rms || []).map(r => [r.id, r])), [rms]);
  const canSave = form.rm_id && form.godown_id && form.batch_no.trim() && Number(form.qty || 0) > 0 && form.opening_date;

  const save = async () => {
    if (!canSave) { toast.error("Fill RM, Godown, Batch No, Quantity, and Opening Date"); return; }
    setSaving(true);
    try {
      // Feb 2026 · Parse the Reserve-for-Order selection ("SO:<id>" | "ESO:<id>")
      // and split into the two typed fields the backend expects.
      let rfo_id = "", rfo_type = "", rfo_no = "";
      if (form.reserved_for_order) {
        const [t, id] = form.reserved_for_order.split(":");
        rfo_type = t; rfo_id = id;
        const match = orders.find(o => o.key === form.reserved_for_order);
        rfo_no = match?.no || "";
      }
      const { data } = await api.post("/opening-stock-entries/", {
        rm_id: form.rm_id,
        godown_id: form.godown_id,
        batch_no: form.batch_no.trim(),
        qty: Number(form.qty),
        opening_date: form.opening_date,
        remarks: form.remarks.trim(),
        reserved_for_sales_order_id: rfo_id,
        reserved_for_sales_order_no: rfo_no,
        reserved_for_type: rfo_type,
      });
      toast.success(`Opening balance posted · ${data.rm_code} · ${fmt(data.qty)} ${data.uom}${rfo_no ? ` · reserved for ${rfo_no}` : ""}`);
      setForm({ rm_id: "", godown_id: "", batch_no: "", qty: "", opening_date: today(), remarks: "", reserved_for_order: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save Opening Stock");
    } finally { setSaving(false); }
  };

  const reverse = async (r) => {
    if (!window.confirm(`Reverse opening entry for ${r.rm_code} · Batch ${r.batch_no} (${fmt(r.qty)} ${r.uom})?\n\nThis deletes the lot + Kardex row and decrements RM current_stock.`)) return;
    try {
      await api.delete(`/opening-stock-entries/${r.id}`);
      toast.success(`Reversed ${r.rm_code} · Batch ${r.batch_no}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reverse failed");
    }
  };

  const totalOpeningQty = entries.reduce((s, r) => s + Number(r.qty || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="opening-stock-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Purchase &amp; Inventory</div>
          <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-2 mt-1">
            <PackageIcon size={28} weight="duotone" className="text-emerald-400" />
            Opening Stock Entry
          </h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Post the go-live raw-material balance for every code. Each entry auto-writes an <b>Opening</b> Kardex row,
            creates a Passed lot at the chosen godown, and bumps <span className="font-mono">raw_materials.current_stock</span>.
            Manual edits on the RM Master are blocked — all opening balances flow through here.
          </p>
        </div>
        <button data-testid="opening-refresh" onClick={load} className="inline-flex items-center gap-1.5 h-9 px-3 border border-[var(--border)] rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-2)]">
          <ArrowsClockwiseIcon size={13} /> Refresh
        </button>
      </div>

      {/* Feb 27, 2026 · Lock banner — visible when Admin has flipped the
          module lock. Regular users see this via a red banner + disabled form. */}
      {locked && (
        <div data-testid="opening-lock-banner" className="border border-red-500/50 bg-red-500/10 rounded-sm p-4 flex items-start gap-3">
          <LockKeyIcon size={22} weight="fill" className="text-red-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-red-300">Module Locked by Administrator</div>
            <div className="text-xs text-zinc-200 mt-1 leading-relaxed">
              Opening Stock Entry is currently locked. Only Administrators can post overrides.
              Use <b>Purchase Receipt (GRN)</b> for daily inward entries.
            </div>
          </div>
        </div>
      )}

      {/* Entry Form */}
      <div className={`border border-[var(--border)] rounded-sm bg-[var(--bg-1)] p-5 ${locked ? "opacity-60 pointer-events-none select-none" : ""}`}>
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-4">New Opening Balance</div>
        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-2">
            <label className={labelCls}>RM Code</label>
            <select data-testid="opening-rm-picker" value={form.rm_id} onChange={e => setForm(f => ({ ...f, rm_id: e.target.value }))} className={inpCls}>
              <option value="">— select RM —</option>
              {rms.map(rm => (
                <option key={rm.id} value={rm.id}>
                  {rm.code}{rm.name ? ` · ${rm.name}` : ""}
                </option>
              ))}
            </select>
            {form.rm_id && rmMap[form.rm_id] && (
              <div className="mt-1 text-[10px] font-mono text-zinc-500">
                UOM: <span className="text-zinc-300">{rmMap[form.rm_id].uom || rmMap[form.rm_id].unit || "KG"}</span>
                &nbsp;·&nbsp;Current stock: <span className="text-zinc-300">{fmt(rmMap[form.rm_id].current_stock)}</span>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Godown / Warehouse</label>
            <select data-testid="opening-godown-picker" value={form.godown_id} onChange={e => setForm(f => ({ ...f, godown_id: e.target.value }))} className={inpCls}>
              <option value="">— select godown —</option>
              {godowns.map(g => (
                <option key={g.id} value={g.id}>
                  {g.code} · {g.name}{g.kind ? ` (${g.kind})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Batch No</label>
            <input data-testid="opening-batch-input" value={form.batch_no} onChange={e => setForm(f => ({ ...f, batch_no: e.target.value.toUpperCase() }))} placeholder="e.g. OPB-2026-001" className={`${inpCls} font-mono`} />
          </div>
          <div>
            <label className={labelCls}>Opening Date</label>
            <input type="date" data-testid="opening-date-input" value={form.opening_date} onChange={e => setForm(f => ({ ...f, opening_date: e.target.value }))} className={inpCls} />
          </div>
          <div>
            <label className={labelCls}>Opening Quantity</label>
            <input type="number" step="any" data-testid="opening-qty-input" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} placeholder="0.000" className={`${inpCls} font-mono text-right`} />
          </div>
          <div className="col-span-5">
            <label className={labelCls}>Remarks (optional)</label>
            <input data-testid="opening-remarks-input" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} placeholder="e.g. Physical count · Y/E close carry-forward" className={inpCls} />
          </div>
          {/* Feb 2026 · Intelligent Auto-Allocation — Reserve this batch for a specific SO/ESO.
              When picked, MRP for that order will pre-tick this lot's allocation. */}
          <div className="col-span-6">
            <label className={labelCls}>Reserve for Order (optional · Auto-allocate on MRP)</label>
            <select data-testid="opening-reserve-for-order" value={form.reserved_for_order}
                    onChange={e => setForm(f => ({ ...f, reserved_for_order: e.target.value }))} className={inpCls}>
              <option value="">— free pool (available to any order) —</option>
              {orders.map(o => (
                <option key={o.key} value={o.key}>
                  [{o.type}] {o.no}{o.customer ? ` · ${o.customer}` : ""}
                </option>
              ))}
            </select>
            {form.reserved_for_order && (
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                ✓ This batch will auto-allocate when MRP is run for the selected order.
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          {/* Feb 27, 2026 · Permanent guidance note — anchors the intent
              of the module even after go-live so operators don't confuse
              it with daily GRN posting. */}
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <WarningIcon size={12} weight="fill" className="text-amber-400" />
            This module is for initial data loading only. Use <b className="text-zinc-300">GRN</b> for daily inward entries.
          </div>
          <button data-testid="opening-save" onClick={save} disabled={!canSave || saving || locked}
            className="inline-flex items-center gap-1.5 h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
            <PlusIcon size={13} weight="bold" />{saving ? "Posting…" : "Post Opening Balance"}
          </button>
        </div>
      </div>

      {/* Report */}
      <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-1)]">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            Opening Stock Register · {entries.length} entries · Total {fmt(totalOpeningQty)}
          </div>
          {loading && <span className="text-[10px] text-zinc-500">Loading…</span>}
        </div>
        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full text-xs" data-testid="opening-entries-table">
            <thead className="sticky top-0 bg-[var(--bg-2)] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">OPB No</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">RM Code</th>
                <th className="px-3 py-2 text-left">RM Name</th>
                <th className="px-3 py-2 text-left">Godown</th>
                <th className="px-3 py-2 text-left">Batch No</th>
                <th className="px-3 py-2 text-right">Quantity</th>
                <th className="px-3 py-2 text-center">UOM</th>
                <th className="px-3 py-2 text-left">Remarks</th>
                <th className="px-3 py-2 text-left">Posted By</th>
                <th className="px-3 py-2 text-center w-16">—</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={11} className="text-center py-8 text-zinc-500 text-xs">
                  <WarningIcon size={22} className="inline-block mr-2 text-amber-400" weight="fill" />
                  No opening balances posted yet — start with the form above.
                </td></tr>
              )}
              {entries.map(r => (
                <tr key={r.id} data-testid={`opening-row-${r.id}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                  <td className="px-3 py-2 font-mono text-[11px] text-[var(--accent)]" data-testid={`opening-opb-${r.id}`}>{r.opb_no || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{r.opening_date}</td>
                  <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.rm_code}</td>
                  <td className="px-3 py-2 text-zinc-300 truncate max-w-[220px]">{r.rm_name}</td>
                  <td className="px-3 py-2 text-zinc-400 text-[11px]">{r.godown_code} · {r.godown_name}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-purple-300">{r.batch_no}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(r.qty)}</td>
                  <td className="px-3 py-2 text-center text-[10px] text-zinc-500">{r.uom}</td>
                  <td className="px-3 py-2 text-zinc-400 text-[11px] truncate max-w-[240px]">{r.remarks || "—"}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 truncate max-w-[160px]">{r.created_by || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <button data-testid={`opening-reverse-${r.id}`} onClick={() => reverse(r)} title="Reverse this opening entry" className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/10 border border-red-500/30">
                      <TrashIcon size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
